import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  organizationsApi,
  sitesApi,
  OrganizationMember,
  OrganizationInvitation,
  OWNER_ROLE_KEY,
  Site,
  extractApiError,
} from '../../../lib/organizations';
import { getHttpStatus } from '../../../lib/api';
import { rbacApi, type CompanyRoleView } from '../../../lib/rbac';
import { useToastStore } from '../../../stores/toast';
import { useMemberEvents } from '../../../hooks/useWebSocket';
import { useEntityChanged } from '../../../hooks/useEntityChanged';
import { useNotificationStore } from '../../../stores/notification';
import { MembersData, MembersModals, MembersHandlers } from './types';

interface UseMembersStateReturn {
  data: MembersData;
  modals: MembersModals;
  handlers: MembersHandlers;
}

/** 초대 모달 역할 멀티셀렉트 기본값 — 시스템 역할 `member`(일반 멤버, 결정 §4 시드). */
const DEFAULT_INVITE_ROLE_KEYS = ['member'];

export function useMembersState(): UseMembersStateReturn {
  const navigate = useNavigate();
  const { currentMembership, user } = useAuthStore();
  const companyId = currentMembership?.companyId;
  /**
   * 이 화면의 축은 **행위별로 셋**이다(company-rbac P4 ① 규약 · P7-g 2026-09-25) — 종전엔 진입 하나를
   * 소유자 성질로 막고 안에서는 아무것도 가리지 않았다.
   *
   * | 행위 | 축 | 서버 게이트 |
   * |---|---|---|
   * | 초대·초대 취소·재발송 | `member.invite` | `@Permissions('member.invite')` |
   * | 정보·역할 수정, 멤버 제거 | `member.manage` | `@Permissions('member.manage')` |
   * | 현장 배정·해제 | **소유자 성질** | 키(`site.assign`) **위에** 서비스가 `isOwnerMember`를 또 본다 |
   *
   * ⚠️ 마지막 줄이 핵심이다 — `sites.service.ts`의 `assignMember`·`unassignMember`는 가드를 통과한 뒤에도
   * `isOwnerMember`를 요구한다(옛 `role === OWNER`를 성질로 옮긴 자리). 키로 바꾸면 `member.manage`
   * 보유자에게 버튼이 뜨고 서버가 거부한다 — 막다른 길이다.
   *
   * 진입은 **관리할 것이 하나라도 있나**로 묻는다(둘 중 아무 키도 없으면 이 화면에서 할 수 있는 일이 없다).
   * 목록 조회 자체(`GET :organizationId/members`)에는 서버 키가 없다.
   */
  const { isOwner, can } = usePermissions();
  const canManageMembers = can('member.manage');
  const canInviteMembers = can('member.invite');
  const canOpenMembersPage = canManageMembers || canInviteMembers;
  const { showToast } = useToastStore();
  const { addNotification } = useNotificationStore();

  // 데이터 상태
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 초대 모달 상태
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteType, setInviteType] = useState<'identifier' | 'phone'>('identifier');
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteAsOwner, setInviteAsOwner] = useState(false);
  const [inviteRoleKeys, setInviteRoleKeys] = useState<string[]>(DEFAULT_INVITE_ROLE_KEYS);
  const [inviteRoleOptions, setInviteRoleOptions] = useState<CompanyRoleView[]>([]);
  const [isLoadingInviteRoles, setIsLoadingInviteRoles] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // 소유자 지정/해제 모달 상태(옛 역할 변경)
  const [roleChangeTarget, setRoleChangeTarget] = useState<OrganizationMember | null>(null);
  const [newIsOwner, setNewIsOwner] = useState(true);
  const [isChangingRole, setIsChangingRole] = useState(false);

  // 제거 모달 상태
  const [removeTarget, setRemoveTarget] = useState<OrganizationMember | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // 초대 취소 모달 상태
  const [cancelTarget, setCancelTarget] = useState<OrganizationInvitation | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // 재발송 상태
  const [isResending, setIsResending] = useState<string | null>(null);

  // 멤버 상세 모달 상태
  const [detailTarget, setDetailTargetRaw] = useState<OrganizationMember | null>(null);
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState<string | null>(null);
  const [assignConfirmSite, setAssignConfirmSite] = useState<Site | null>(null);
  const [unassignConfirmSiteId, setUnassignConfirmSiteId] = useState<string | null>(null);

  // 이관 모달 상태
  const [transferRequired, setTransferRequired] = useState<{
    siteId: string;
    siteName: string;
    activeInspectionCount: number;
  } | null>(null);
  const [transferTargetMemberId, setTransferTargetMemberId] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);

  // 권한 체크 — 관리 행위가 하나도 없으면 들어올 이유가 없다(위 표).
  useEffect(() => {
    if (!canOpenMembersPage) {
      navigate('/');
    }
  }, [canOpenMembersPage, navigate]);

  // 데이터 로드
  // silent: 실시간 재조회(ENTITY_CHANGED) 경로 — 페이지를 스피너로 갈아 끼우지 않고 목록만 새로 채운다.
  const loadData = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!companyId) return;

    if (!silent) setIsLoading(true);
    try {
      // ⚠️ 초대 목록은 **키가 있을 때만** 부른다 — 서버 `GET :organizationId/invitations`가
      // `@Permissions('member.invite')`라, `member.manage`만 가진 사람이 부르면 403이 나고
      // `Promise.all`이 함께 깨져 **멤버 목록까지** 못 그린다(화면 전체가 에러가 된다).
      const [membersData, invitationsData] = await Promise.all([
        organizationsApi.getMembers(companyId),
        canInviteMembers ? organizationsApi.getInvitations(companyId) : Promise.resolve([]),
      ]);
      setMembers(membersData);
      setInvitations(invitationsData);
      // 멤버 상세 모달(`detailTarget`)은 목록과 **별도 state**라, 목록만 갈아 끼우면 뒤 목록은 새 값인데
      // 열어 둔 모달은 옛 값으로 남는다. 이 화면의 규약대로 **새 목록에서 id로 다시 집는다**
      // (`handleConfirmAssign`·`handleConfirmUnassign`·`refreshMembersAndDetail`이 모두 그렇게 한다 —
      //  상세 모달은 편집 폼이 아니라 즉시 저장형이라 `paused`로 미룰 축이 아니다).
      // 새 목록에 없으면(다른 사람이 그 멤버를 제거) 옛 값을 유지한다 — 위 세 곳과 같은 규약이다.
      // ⚠️ 함수형 업데이트로 읽는다. `detailTarget`을 deps에 넣으면 `loadData`가 매번 새로 만들어져
      //    초기 로드 effect(`[companyId, loadData]`)가 상세 모달을 열고 닫을 때마다 다시 돈다.
      setDetailTargetRaw((prev) => (prev ? (membersData.find((m) => m.id === prev.id) ?? prev) : prev));
    } catch (err) {
      const apiError = extractApiError(err);
      // silent 재조회(실시간 이벤트)의 실패는 알리지 않는다 — 사용자가 아무것도 하지 않았는데
      // 에러 토스트가 뜨면 자기 행동이 실패한 줄 안다. 다음 이벤트·다음 진입에서 복구된다.
      if (!silent) showToast(apiError.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, showToast, canInviteMembers]);

  // 초기 데이터 로드
  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId, loadData]);

  // 멤버 이벤트 실시간 구독
  useMemberEvents({
    onJoined: () => loadData(),
    onLeft: () => loadData(),
    onRoleChanged: () => loadData(),
  });

  // 멤버 행 자체의 변경(ENTITY_CHANGED member — 프로필·연락처 등, 위 합류/퇴장/역할 이벤트 밖의 것)을 조용히
  // 반영. 초대·역할 변경·제거·초대 취소 모달 중 하나라도 열려 있으면 미루고 닫힐 때 1회 재조회.
  // 📌 멤버 **상세** 모달은 이 목록에 없다 — 입력을 담은 폼이 아니라 즉시 저장형이라 덮어쓸 것이
  // 없고, 오히려 옛 값이 남는 쪽이 결함이다. 그래서 미루지 않고 `loadData`가 새 목록에서 id로
  // 다시 집는다(위 loadData 주석).
  useEntityChanged(['member'], () => loadData({ silent: true }), {
    paused: showInviteModal || roleChangeTarget !== null || removeTarget !== null || cancelTarget !== null,
  });

  // 초대 모달이 열릴 때 조직 역할 목록(멀티셀렉트 후보)을 싣는다 — 소유자 역할은 「소유자로 초대」 토글이 맡으므로 뺀다.
  // 실패는 조용히 둔다(후보 없이도 기본 `member`로 초대는 된다).
  useEffect(() => {
    if (!showInviteModal || !companyId) return;
    let cancelled = false;
    setIsLoadingInviteRoles(true);
    rbacApi
      .getRoles(companyId)
      .then((roles) => {
        if (!cancelled) setInviteRoleOptions(roles.filter((r) => !r.isOwner));
      })
      .catch(() => {
        if (!cancelled) setInviteRoleOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingInviteRoles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showInviteModal, companyId]);

  const toggleInviteRoleKey = (roleKey: string) => {
    setInviteRoleKeys((prev) => (prev.includes(roleKey) ? prev.filter((k) => k !== roleKey) : [...prev, roleKey]));
  };

  // 핸들러: 초대
  const handleInvite = async () => {
    if (!companyId) return;

    const value = inviteType === 'identifier'
      ? inviteIdentifier.trim()
      : invitePhone.trim();

    if (!value) return;

    setIsInviting(true);
    try {
      // 역할 축은 `roleKeys` 하나(P7-b-2) — 소유자 초대는 시스템 소유자 역할 key 하나를 싣는다(서버 S15: 기존 가입자라도
      // 초대장 → 수락이 동의). 그 외는 멀티셀렉트 값(비면 서버가 `member`).
      const inviteData: { identifier?: string; phone?: string; roleKeys: string[] } = {
        roleKeys: inviteAsOwner ? [OWNER_ROLE_KEY] : inviteRoleKeys,
      };

      if (inviteType === 'identifier') {
        inviteData.identifier = value;
      } else {
        inviteData.phone = value;
      }

      const result = await organizationsApi.inviteMember(companyId, inviteData);

      if (result.type === 'MEMBER_ADDED' && result.member) {
        setMembers((prev) => [...prev, result.member!]);
      } else if (result.type === 'INVITATION_CREATED' && result.invitation) {
        setInvitations((prev) => [...prev, result.invitation!]);
        addNotification({
          type: 'invitation_sent',
          title: '초대 발송',
          message: `${result.invitation.email || result.invitation.phone || result.invitation.username}님에게 초대를 발송했습니다.`,
          data: { companyId, invitationId: result.invitation.id },
        });
      }

      // 입력창만 초기화 (모달 유지)
      setInviteIdentifier('');
      setInvitePhone('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const resetInviteForm = () => {
    setShowInviteModal(false);
    setInviteIdentifier('');
    setInvitePhone('');
    setInviteType('identifier');
    setInviteAsOwner(false);
    setInviteRoleKeys(DEFAULT_INVITE_ROLE_KEYS);
  };

  // 핸들러: 소유자 지정/해제
  const handleRoleChange = async () => {
    if (!companyId || !roleChangeTarget) return;

    setIsChangingRole(true);
    try {
      const updated = await organizationsApi.updateMemberRole(
        companyId,
        roleChangeTarget.id,
        { isOwner: newIsOwner }
      );
      setMembers((prev) =>
        prev.map((m) => (m.id === updated.id ? updated : m))
      );
      setRoleChangeTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsChangingRole(false);
    }
  };

  // 핸들러: 제거
  const handleRemove = async () => {
    if (!companyId || !removeTarget) return;

    setIsRemoving(true);
    try {
      await organizationsApi.removeMember(companyId, removeTarget.id);
      setMembers((prev) => prev.filter((m) => m.id !== removeTarget.id));
      setRemoveTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  // 핸들러: 초대 취소
  const handleCancelInvitation = async () => {
    if (!companyId || !cancelTarget) return;

    setIsCancelling(true);
    try {
      await organizationsApi.cancelInvitation(companyId, cancelTarget.id);
      setInvitations((prev) => prev.filter((inv) => inv.id !== cancelTarget.id));
      setCancelTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // 핸들러: 멤버 상세 모달 열기
  const setDetailTarget = useCallback(async (member: OrganizationMember | null) => {
    setDetailTargetRaw(member);
    setAssignConfirmSite(null);
    setUnassignConfirmSiteId(null);
    if (member && companyId) {
      setIsLoadingSites(true);
      try {
        const sites = await sitesApi.findAll(companyId);
        setAllSites(sites);
      } catch (err) {
        const apiError = extractApiError(err);
        showToast(apiError.message, 'error');
      } finally {
        setIsLoadingSites(false);
      }
    }
  }, [companyId, showToast]);

  // 핸들러: 현장 할당 (확인 모달 표시)
  const handleAssignSite = useCallback((site: Site) => {
    setAssignConfirmSite(site);
  }, []);

  // 핸들러: 현장 할당 확인
  const handleConfirmAssign = useCallback(async () => {
    if (!companyId || !detailTarget || !assignConfirmSite) return;

    setIsAssigning(true);
    try {
      await sitesApi.assignMember(companyId, assignConfirmSite.id, {
        memberId: detailTarget.id,
      });
      // 멤버 목록 새로고침하여 siteAssignments 갱신
      const updatedMembers = await organizationsApi.getMembers(companyId);
      setMembers(updatedMembers);
      // detailTarget도 갱신
      const updated = updatedMembers.find(m => m.id === detailTarget.id);
      if (updated) setDetailTargetRaw(updated);
      setAssignConfirmSite(null);
      showToast(`${assignConfirmSite.name} 현장에 할당했습니다.`, 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsAssigning(false);
    }
  }, [companyId, detailTarget, assignConfirmSite, showToast]);

  // 핸들러: 현장 할당 취소
  const handleCancelAssign = useCallback(() => {
    setAssignConfirmSite(null);
  }, []);

  // 핸들러: 현장 해제 (확인 모달 표시)
  const handleUnassignSite = useCallback((siteId: string) => {
    setUnassignConfirmSiteId(siteId);
  }, []);

  // 핸들러: 현장 해제 확인
  const handleConfirmUnassign = useCallback(async () => {
    if (!companyId || !detailTarget || !unassignConfirmSiteId) return;

    setIsUnassigning(unassignConfirmSiteId);
    try {
      await sitesApi.unassignMember(companyId, unassignConfirmSiteId, detailTarget.id);
      const updatedMembers = await organizationsApi.getMembers(companyId);
      setMembers(updatedMembers);
      const updated = updatedMembers.find(m => m.id === detailTarget.id);
      if (updated) setDetailTargetRaw(updated);
      setUnassignConfirmSiteId(null);
      showToast('현장 할당을 해제했습니다.', 'success');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      const httpStatus = getHttpStatus(err);
      // 409 = 진행중인 점검이 있음 → 이관 모달 표시
      if (httpStatus === 409 && apiError.code === 'SITE_UNASSIGN_ACTIVE_INSPECTIONS') {
        const siteName = allSites.find(s => s.id === unassignConfirmSiteId)?.name || '';
        setTransferRequired({
          siteId: unassignConfirmSiteId,
          siteName,
          activeInspectionCount: (apiError.details?.activeInspectionCount as number) || 0,
        });
        setUnassignConfirmSiteId(null);
      } else {
        showToast(apiError.message, 'error');
      }
    } finally {
      setIsUnassigning(null);
    }
  }, [companyId, detailTarget, unassignConfirmSiteId, allSites, showToast]);

  // 핸들러: 현장 해제 취소
  const handleCancelUnassign = useCallback(() => {
    setUnassignConfirmSiteId(null);
  }, []);

  // 멤버 목록만 갱신 — 상세 모달과 무관한 경로(중복 정리 병합 후 등)
  const refreshMemberList = useCallback(async () => {
    if (!companyId) return;
    const updatedMembers = await organizationsApi.getMembers(companyId);
    setMembers(updatedMembers);
  }, [companyId]);

  // 멤버 목록 + detailTarget 갱신 헬퍼
  const refreshMembersAndDetail = useCallback(async () => {
    if (!companyId || !detailTarget) return;
    const updatedMembers = await organizationsApi.getMembers(companyId);
    setMembers(updatedMembers);
    const updated = updatedMembers.find(m => m.id === detailTarget.id);
    if (updated) setDetailTargetRaw(updated);
  }, [companyId, detailTarget]);

  // 핸들러: 이관 후 해제
  const handleTransferAndUnassign = useCallback(async () => {
    if (!companyId || !detailTarget || !transferRequired || !transferTargetMemberId) return;

    setIsTransferring(true);
    try {
      await sitesApi.unassignMember(companyId, transferRequired.siteId, detailTarget.id, {
        transferToMemberId: transferTargetMemberId,
      });
      await refreshMembersAndDetail();
      setTransferRequired(null);
      setTransferTargetMemberId(null);
      showToast('점검을 이관하고 현장 할당을 해제했습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsTransferring(false);
    }
  }, [companyId, detailTarget, transferRequired, transferTargetMemberId, refreshMembersAndDetail, showToast]);

  // 핸들러: 강제 해제 (이관 없이)
  const handleForceUnassign = useCallback(async () => {
    if (!companyId || !detailTarget || !transferRequired) return;

    setIsTransferring(true);
    try {
      await sitesApi.unassignMember(companyId, transferRequired.siteId, detailTarget.id, {
        force: true,
      });
      await refreshMembersAndDetail();
      setTransferRequired(null);
      setTransferTargetMemberId(null);
      showToast('현장 할당을 강제 해제했습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsTransferring(false);
    }
  }, [companyId, detailTarget, transferRequired, refreshMembersAndDetail, showToast]);

  // 핸들러: 이관 모달 취소
  const handleCancelTransfer = useCallback(() => {
    setTransferRequired(null);
    setTransferTargetMemberId(null);
  }, []);

  // (옛 멤버 세부 권한 토글 handleUpdatePermission — canManageSites/Inspections — 은 P7-a에서 서버 컬럼과 함께 제거.
  //  멤버별 권한은 역할 배정으로 편집한다)

  // 핸들러: 초대 재발송
  const handleResendInvitation = async (invitation: OrganizationInvitation) => {
    if (!companyId) return;

    setIsResending(invitation.id);
    try {
      const updated = await organizationsApi.resendInvitation(companyId, invitation.id);
      setInvitations((prev) =>
        prev.map((inv) => (inv.id === updated.id ? updated : inv))
      );
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsResending(null);
    }
  };

  return {
    data: {
      members,
      invitations,
      isLoading,
      currentUserId: user?.id,
      isOwner,
      canManageMembers,
      canInviteMembers,
    },
    modals: {
      showInviteModal,
      inviteType,
      inviteIdentifier,
      invitePhone,
      inviteAsOwner,
      inviteRoleKeys,
      inviteRoleOptions,
      isLoadingInviteRoles,
      isInviting,
      roleChangeTarget,
      newIsOwner,
      isChangingRole,
      removeTarget,
      isRemoving,
      cancelTarget,
      isCancelling,
      isResending,
      detailTarget,
      allSites,
      isLoadingSites,
      isAssigning,
      isUnassigning,
      assignConfirmSite,
      unassignConfirmSiteId,
      transferRequired,
      transferTargetMemberId,
      isTransferring,
    },
    handlers: {
      handleInvite,
      setShowInviteModal,
      setInviteType,
      setInviteIdentifier,
      setInvitePhone,
      setInviteAsOwner,
      toggleInviteRoleKey,
      resetInviteForm,
      handleRoleChange,
      setRoleChangeTarget,
      setNewIsOwner,
      handleRemove,
      setRemoveTarget,
      handleCancelInvitation,
      setCancelTarget,
      handleResendInvitation,
      setDetailTarget,
      handleAssignSite,
      handleConfirmAssign,
      handleCancelAssign,
      handleUnassignSite,
      handleConfirmUnassign,
      handleCancelUnassign,
      handleTransferAndUnassign,
      handleForceUnassign,
      handleCancelTransfer,
      setTransferTargetMemberId,
      refreshMembers: refreshMembersAndDetail,
      refreshMemberList,
    },
  };
}
